/**
 * The admin's edits to the exercise list (B-66, D-118): exercises added from the portal,
 * and new names for any exercise. Pure — the clients load the edits from Firestore and
 * pass them in; this decides what is valid and installs the result.
 *
 * What an edit may NOT do is the point of the design. An exercise's factor and measure
 * are fixed once it exists, because `score` is denormalised onto every workout (C-11):
 * changing a factor would silently change past days on one client and not the other.
 * So a factor is chosen once, at creation, and only a name and other names change after.
 * `firestore.rules` enforces the same, for a client that skips this module.
 *
 * Ids: a built-in keeps its hand-assigned id (D-31); an added exercise gets
 * `custom-<slug of its first name>`, permanent even if it is renamed later, and never
 * colliding with a built-in, whose ids never start with "custom-".
 */
import { MUSCLE_GROUPS } from "./models.js";
import { BUILT_IN_CATALOG, CATALOG, installedAliases, replaceCatalog } from "./catalog.js";
import { aliasEntries, matchExercise, normalise } from "./matcher.js";
/** The one account that may edit the list. `firestore.rules` holds the same number. */
export const ADMIN_PHONE = "+917503758091";
export const NAME_BOUNDS = { min: 2, max: 60 };
export const MAX_ALIASES = 10;
/** The same ceiling `catalog.test.ts` holds every built-in duration exercise to (D-81). */
export const MAX_DURATION_FACTOR = 0.15;
/** Pull-ups and dips carry the whole body; nothing carries more. */
export const MAX_FACTOR = 1;
const CUSTOM_PREFIX = "custom-";
/** Trims and collapses inner whitespace, so "  Half   Crunch " is "Half Crunch". */
export function tidyName(input) {
    return input.trim().replace(/\s+/g, " ");
}
function squash(input) {
    return normalise(input).replace(/ /g, "");
}
/** `custom-<slug>`, suffixed `-2`, `-3`… when that id is already taken. */
export function customExerciseId(name, taken) {
    const slug = normalise(name)
        .replace(/[^a-z0-9 ]/g, "")
        .trim()
        .replace(/ +/g, "-")
        .slice(0, 50)
        .replace(/-+$/, "") || "exercise";
    const base = `${CUSTOM_PREFIX}${slug}`;
    if (!taken.has(base))
        return base;
    for (let n = 2;; n += 1)
        if (!taken.has(`${base}-${n}`))
            return `${base}-${n}`;
}
export function isCustomId(id) {
    return id.startsWith(CUSTOM_PREFIX);
}
/** Every phrase that already finds an exercise, squashed, with the exercise it finds. */
function phrasesInUse() {
    const used = new Map();
    for (const e of CATALOG)
        used.set(squash(e.name), e.id);
    for (const [alias, id] of [...installedAliases(), ...aliasEntries()])
        used.set(squash(alias), id);
    return used;
}
function nameProblem(name, label) {
    if (name.length < NAME_BOUNDS.min)
        return `${label} is too short.`;
    if (name.length > NAME_BOUNDS.max)
        return `${label} is longer than ${NAME_BOUNDS.max} characters.`;
    if (!/[a-z]/i.test(name))
        return `${label} needs at least one letter.`;
    return null;
}
/**
 * The problems with a phrase that should find exercise `selfId`, or none. A phrase that
 * already finds a DIFFERENT exercise is refused: two exercises answering to one name is a
 * question in WhatsApp at best, and a confident wrong answer at worst (D-76).
 */
function phraseProblems(phrases, selfId) {
    const used = phrasesInUse();
    const problems = [];
    const seen = new Set();
    for (const phrase of phrases) {
        const key = squash(phrase);
        if (seen.has(key)) {
            problems.push(`"${phrase}" is listed twice.`);
            continue;
        }
        seen.add(key);
        const owner = used.get(key);
        if (owner !== undefined && owner !== selfId) {
            const other = CATALOG.find((e) => e.id === owner)?.name ?? owner;
            problems.push(`"${phrase}" already finds ${other}.`);
        }
    }
    return problems;
}
function aliasProblems(aliases, selfId) {
    const problems = [];
    if (aliases.length > MAX_ALIASES)
        problems.push(`At most ${MAX_ALIASES} other names.`);
    for (const alias of aliases) {
        const p = nameProblem(alias, `"${alias}"`);
        if (p) {
            problems.push(p);
            continue;
        }
        /*
         * An other name is looked up before the fuzzy stage, so it would quietly take a phrase
         * away from whatever it finds today: "pendlay" as another name for a landmine row would
         * stop finding the Pendlay Row, with nothing to say so (D-76). Only a CONFIDENT match
         * elsewhere is refused — a phrase that today only asks a question may be claimed, which
         * is what an other name is for. A NAME is not held to this: a new exercise's own name
         * is often a near miss of an existing one, and that is the point of adding it.
         */
        const now = matchExercise(alias);
        if (now.kind === "match" && now.exercise.id !== selfId) {
            problems.push(`"${alias}" already finds ${now.exercise.name}.`);
        }
    }
    return problems;
}
/**
 * Validates a new exercise against the catalog as currently installed and, when it is
 * valid, returns it with its id. The name and aliases are tidied, never rejected for
 * spacing.
 */
export function prepareNewExercise(input) {
    const name = tidyName(input.name);
    const aliases = input.aliases.map(tidyName).filter((a) => a.length > 0);
    const problems = [];
    const np = nameProblem(name, "The name");
    if (np)
        problems.push(np);
    if (!MUSCLE_GROUPS.includes(input.muscleGroup))
        problems.push("Pick a muscle group.");
    if (input.measure !== "reps" && input.measure !== "duration")
        problems.push("Pick reps or time.");
    const f = input.bodyweightFactor;
    if (!Number.isFinite(f) || f < 0)
        problems.push("The bodyweight factor must be 0 or more.");
    else if (f > MAX_FACTOR)
        problems.push(`The bodyweight factor cannot be more than ${MAX_FACTOR}.`);
    else if (Math.round(f * 100) !== f * 100)
        problems.push("Use at most two decimals for the factor.");
    else if (input.measure === "duration" && f > MAX_DURATION_FACTOR) {
        // D-81: score multiplies load by time, and time-based work is long. This mistake was
        // made four times by hand before the ceiling existed; it is not made a fifth here.
        problems.push(`A timed exercise's factor cannot be more than ${MAX_DURATION_FACTOR}.`);
    }
    // The name's own clash first: it is the likelier mistake, and the one to read first.
    if (problems.length === 0)
        problems.push(...phraseProblems([name], null));
    problems.push(...aliasProblems(aliases, null));
    if (problems.length === 0)
        problems.push(...phraseProblems([name, ...aliases], null));
    if (problems.length > 0)
        return { ok: false, problems };
    const id = customExerciseId(name, new Set(CATALOG.map((e) => e.id)));
    return {
        ok: true,
        exercise: { id, name, muscleGroup: input.muscleGroup, bodyweightFactor: f, measure: input.measure, aliases },
    };
}
/**
 * Validates a new name (and, for an added exercise, its other names) for exercise `id`.
 * A built-in has no other names to edit here — its aliases are the reviewed table in
 * matcher.ts.
 */
export function prepareRename(id, newName, aliases = []) {
    const name = tidyName(newName);
    const tidied = aliases.map(tidyName).filter((a) => a.length > 0);
    const problems = [];
    if (!CATALOG.some((e) => e.id === id))
        problems.push("That exercise is not in the list.");
    const np = nameProblem(name, "The name");
    if (np)
        problems.push(np);
    if (!isCustomId(id) && tidied.length > 0)
        problems.push("Only an added exercise has other names here.");
    if (problems.length === 0)
        problems.push(...phraseProblems([name], id));
    problems.push(...aliasProblems(tidied, id));
    if (problems.length === 0)
        problems.push(...phraseProblems([name, ...tidied], id));
    return problems.length > 0 ? { ok: false, problems } : { ok: true, name, aliases: tidied };
}
/** The shape checks a stored added exercise must pass to be installed at all. */
function isInstallable(e) {
    return (typeof e?.id === "string" &&
        isCustomId(e.id) &&
        typeof e.name === "string" &&
        nameProblem(tidyName(e.name), "") === null &&
        MUSCLE_GROUPS.includes(e.muscleGroup) &&
        (e.measure === "reps" || e.measure === "duration") &&
        typeof e.bodyweightFactor === "number" &&
        Number.isFinite(e.bodyweightFactor) &&
        e.bodyweightFactor >= 0 &&
        e.bodyweightFactor <= (e.measure === "duration" ? MAX_DURATION_FACTOR : MAX_FACTOR));
}
/**
 * Installs the admin's edits over the built-ins and returns the ids it refused. Always
 * starts again from the built-ins, so installing the same edits twice is a no-op and a
 * deleted rename is undone.
 *
 * Defensive on purpose: the rules check the shape of what is stored, but this runs on
 * every client against whatever is there, and a bad document must cost one exercise,
 * never the catalog.
 */
export function installCatalog(edits) {
    const refused = [];
    const builtInIds = new Set(BUILT_IN_CATALOG.map((e) => e.id));
    const aliases = [];
    const entries = BUILT_IN_CATALOG.map((e) => {
        const renamed = edits.renamed[e.id];
        if (typeof renamed !== "string")
            return e;
        const name = tidyName(renamed);
        if (nameProblem(name, "") !== null) {
            refused.push(e.id);
            return e;
        }
        // The original name keeps finding it: people, and the bot's users, still say it.
        if (squash(name) !== squash(e.name))
            aliases.push([e.name, e.id]);
        return { ...e, name };
    });
    const seen = new Set();
    const sorted = [...edits.added].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    for (const e of sorted) {
        if (!isInstallable(e) || builtInIds.has(e.id) || seen.has(e.id)) {
            refused.push(typeof e?.id === "string" ? e.id : "(no id)");
            continue;
        }
        seen.add(e.id);
        entries.push({
            id: e.id,
            name: tidyName(e.name),
            muscleGroup: e.muscleGroup,
            bodyweightFactor: e.bodyweightFactor,
            measure: e.measure,
        });
        for (const alias of Array.isArray(e.aliases) ? e.aliases : []) {
            if (typeof alias === "string" && nameProblem(tidyName(alias), "") === null) {
                aliases.push([tidyName(alias), e.id]);
            }
        }
    }
    // Grouped as the built-ins are, so an added curl lists with the curls.
    const order = new Map(MUSCLE_GROUPS.map((g, i) => [g, i]));
    const stable = entries.map((e, i) => ({ e, i }));
    stable.sort((a, b) => order.get(a.e.muscleGroup) - order.get(b.e.muscleGroup) || a.i - b.i);
    replaceCatalog(stable.map(({ e }) => e), aliases);
    return refused;
}
/** Back to the built-ins alone. For tests, and for a signed-out client. */
export function resetCatalog() {
    replaceCatalog(BUILT_IN_CATALOG, []);
}
//# sourceMappingURL=catalogEdits.js.map